/**
 * The "From the field" toolbar chip (approved mockup, pick 4).
 *
 * Only adds where the tech turned on "Office needs to see this" show here,
 * until someone marks them read. It is the ONLY place a flagged add is
 * announced: no bell, no phone ping, no toast. Hidden when there is nothing to
 * read (calm by default), and for anyone the field_add switch is off for. The
 * chip and the flyout read one server list (GET /api/field-adds/inbox).
 */
import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useMutation, useQuery, useQueryClient } from "@tanstack/react-query";
import { Flag, X } from "lucide-react";
import { api } from "@/lib/api";
import { kindPathOf, type FieldAddItem } from "./field-add-api";
import { PlacePill, deviceName, shortDate, shortTime } from "./places";

export const FIELD_INBOX_KEY = ["field-adds", "inbox"] as const;

/** Where "Open" goes: the page the add belongs to. */
export function openPathFor(item: FieldAddItem): string {
    if (item.target === "device" && item.assetId) return `/assets/${item.assetId}`;
    if (item.target === "location" && item.siteId) return `/sites/${item.siteId}`;
    if (item.target === "customer" && item.customerId) return `/customers/${item.customerId}`;
    return item.jobId ? `/jobs/${item.jobId}#from-the-field` : "/";
}

function titleFor(item: FieldAddItem): string {
    const where =
        item.target === "device"
            ? deviceName(item)
            : item.target === "customer"
              ? `${item.customerName ?? "Customer"} · customer note`
              : `${item.siteName ?? "Location"}${item.target === "location" ? " · location note" : ""}`;
    return item.target === "device" ? `${item.siteName ?? "Location"} · ${where}` : where;
}

export function FieldChip() {
    const qc = useQueryClient();
    const q = useQuery({
        queryKey: FIELD_INBOX_KEY,
        queryFn: () => api.get<{ on: boolean; count: number; items: FieldAddItem[] }>("/api/field-adds/inbox"),
        refetchInterval: 60_000,
        refetchOnWindowFocus: true,
        staleTime: 30_000,
        retry: false,
    });
    const [open, setOpen] = useState(false);
    useEffect(() => {
        if (!open) return;
        const onKey = (e: KeyboardEvent) => e.key === "Escape" && setOpen(false);
        window.addEventListener("keydown", onKey);
        return () => window.removeEventListener("keydown", onKey);
    }, [open]);
    const read = useMutation({
        mutationFn: (item: FieldAddItem) => api.post(`/api/field-adds/${kindPathOf(item)}/${item.id}/read`, {}),
        onSettled: () => void qc.invalidateQueries({ queryKey: ["field-adds"] }),
    });
    const items = q.data?.on ? (q.data.items ?? []) : [];
    if (!q.data?.on || (items.length === 0 && !open)) return null;
    return (
        <>
            <button
                type="button"
                className="ffchip"
                data-testid="field-chip"
                aria-expanded={open}
                aria-haspopup="dialog"
                aria-label={`From the field: ${items.length} to read`}
                onClick={() => setOpen((o) => !o)}
            >
                <Flag size={15} aria-hidden />
                <span>From the field · {items.length}</span>
            </button>
            <aside
                className={`fffly${open ? " on" : ""}`}
                role="dialog"
                aria-label="From the field"
                aria-hidden={!open}
                {...((open ? {} : { inert: "" }) as Record<string, string>)}
            >
                <div className="fffly-h">
                    <b>From the field</b>
                    <button type="button" className="x" aria-label="Close" onClick={() => setOpen(false)}>
                        <X size={16} />
                    </button>
                </div>
                <div className="fffly-b">
                    <p className="sub">
                        Only adds where the tech turned on “Office needs to see this” show here, until someone marks
                        them read.
                    </p>
                    {items.length === 0 ? (
                        <p className="sub">All read. New ones show up here.</p>
                    ) : (
                        items.map((item) => (
                            <article key={item.id} className="ffrow" data-testid="field-chip-row">
                                <PlacePill item={item} />
                                <b>{titleFor(item)}</b>
                                <span className="meta">
                                    {item.authorName ?? "A tech"} · {shortDate(item.createdAt)} ·{" "}
                                    {shortTime(item.createdAt)} · {item.kind}
                                    {item.jobNumber ? ` · Job #${item.jobNumber}` : ""}
                                </span>
                                {item.kind === "note" && item.body && <span className="body">“{item.body}”</span>}
                                <span className="acts">
                                    <Link to={openPathFor(item)} className="fb" onClick={() => setOpen(false)}>
                                        Open
                                    </Link>
                                    <button
                                        type="button"
                                        className="fb main"
                                        disabled={read.isPending}
                                        onClick={() => read.mutate(item)}
                                    >
                                        Mark read
                                    </button>
                                </span>
                            </article>
                        ))
                    )}
                </div>
            </aside>
        </>
    );
}

/** Scoped to the toolbar (.tbv3), like the booking chip. */
export const fieldChipCss = `
  .tbv3 .ffchip{display:inline-flex;align-items:center;gap:7px;height:34px;padding:0 12px;border-radius:10px;font-weight:700;
    font-size:13px;cursor:pointer;white-space:nowrap;border:1px solid #B3261E;background:#FDE4E2;color:#8C1D18;font-variant-numeric:tabular-nums}
  .tbv3 .ffchip:focus-visible{outline:3px solid #8fb4ee;outline-offset:2px}
  .tbv3 .fffly{position:fixed;top:0;right:0;bottom:0;width:410px;max-width:100vw;background:#FCFAF6;border-left:1px solid #E7DFD2;
    box-shadow:-18px 0 40px -24px rgba(30,25,15,.45);display:grid;grid-template-rows:auto 1fr;color:#14213a;z-index:60;
    transform:translateX(105%);visibility:hidden;transition:transform .22s ease,visibility 0s linear .22s}
  .tbv3 .fffly.on{transform:translateX(0);visibility:visible;transition:transform .22s ease}
  @media (prefers-reduced-motion:reduce){.tbv3 .fffly,.tbv3 .fffly.on{transition:none}}
  .tbv3 .fffly-h{display:flex;align-items:center;gap:10px;padding:14px 16px;border-bottom:1px solid #E7DFD2}
  .tbv3 .fffly-h b{font-size:18px;flex:1;font-weight:750}
  .tbv3 .fffly-h .x{width:30px;height:30px;border-radius:8px;border:1px solid #E7DFD2;background:#fff;cursor:pointer;display:grid;place-items:center;color:#14213a}
  .tbv3 .fffly-b{overflow-y:auto;padding:12px 14px 18px;display:flex;flex-direction:column;gap:10px;min-height:0}
  .tbv3 .fffly-b>*{flex:none}
  .tbv3 .fffly .sub{font-size:12.5px;color:#5d6b7e}
  .tbv3 .ffrow{background:#fff;border:1px solid #E7DFD2;border-left:6px solid #B3261E;border-radius:12px;padding:11px 12px;display:grid;gap:5px;justify-items:start}
  .tbv3 .ffrow b{font-size:14px}
  .tbv3 .ffrow .meta{font-size:12px;color:#5d6b7e}
  .tbv3 .ffrow .body{font-size:13px;white-space:pre-wrap}
  .tbv3 .ffrow .acts{display:flex;gap:8px;margin-top:3px}
  .tbv3 .ffrow .fb{height:30px;padding:0 12px;border-radius:8px;border:1px solid #E7DFD2;background:#fff;font-size:12.5px;font-weight:700;
    display:inline-flex;align-items:center;color:#14213a;cursor:pointer;text-decoration:none}
  .tbv3 .ffrow .fb.main{background:#14213a;border-color:#14213a;color:#fff}
`;
